(function initializeInkNfiberCardSwatches(globalObject, factory) {
  const api = factory(globalObject);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (globalObject?.document) {
    globalObject.InkNfiberCardSwatches = api;
    api.install(globalObject.document);
  }
})(typeof window !== 'undefined' ? window : null, function createInkNfiberCardSwatches(environment) {
  'use strict';

  const ROOT_SELECTOR = '[data-inknfiber-card-swatches]';
  const SWATCH_SELECTOR = '[data-inknfiber-card-swatch]';
  const TOGGLE_SELECTOR = '[data-inknfiber-card-swatches-toggle]';
  const CARD_SELECTOR = '[data-inknfiber-swatch-card]';
  const MEDIA_CONTAINER_SELECTOR = '[data-inknfiber-card-image-container]';
  const SWAP_CLASS = 'inknfiber-card-image--swapped';
  const LOADING_CLASS = 'inknfiber-card-image-is-loading';

  function setAttributeOrRemove(element, name, value) {
    if (!element) return;
    if (value == null || value === '') element.removeAttribute(name);
    else element.setAttribute(name, String(value));
  }

  function descriptorKey(descriptor) {
    return [
      descriptor?.mediaId || '',
      descriptor?.variantId || '',
      descriptor?.src || '',
    ].join('|');
  }

  function descriptorFromButton(button) {
    return {
      mediaId: button?.dataset?.mediaId || '',
      variantId: button?.dataset?.variantId || '',
      src: button?.dataset?.imageSrc || '',
      srcset: button?.dataset?.imageSrcset || '',
      sizes: button?.dataset?.imageSizes || '',
      alt: button?.dataset?.imageAlt || '',
      width: button?.dataset?.imageWidth || '',
      height: button?.dataset?.imageHeight || '',
    };
  }

  function imageDescriptor(image, mediaId = '', variantId = '') {
    return {
      mediaId: mediaId || image?.dataset?.mediaId || '',
      variantId: variantId || image?.dataset?.variantId || '',
      src: image?.getAttribute?.('src') || '',
      srcset: image?.getAttribute?.('srcset') || '',
      sizes: image?.getAttribute?.('sizes') || '',
      alt: image?.getAttribute?.('alt') || '',
      width: image?.getAttribute?.('width') || '',
      height: image?.getAttribute?.('height') || '',
    };
  }

  function defaultImageLoader(descriptor) {
    if (!descriptor?.src || !environment?.Image) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const image = new environment.Image();
      if (descriptor.sizes) image.sizes = descriptor.sizes;
      if (descriptor.srcset) image.srcset = descriptor.srcset;
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`Unable to load card image: ${descriptor.src}`));
      image.src = descriptor.src;
    });
  }

  class CardMediaAdapter {
    constructor(card) {
      this.card = card;
      this.mediaContainer = card?.querySelector?.(MEDIA_CONTAINER_SELECTOR) || null;
      this.slideshow = this.mediaContainer?.querySelector?.('slideshow-component') || null;
    }

    activeSlide() {
      return this.mediaContainer?.querySelector?.('slideshow-slide[aria-hidden="false"]') || null;
    }

    currentImage() {
      return this.activeSlide()?.querySelector?.('img') || this.mediaContainer?.querySelector?.('img') || null;
    }

    originalDescriptor() {
      const slide = this.activeSlide();
      const image = this.currentImage();
      const mediaId =
        this.card?.dataset?.inknfiberInitialMediaId ||
        slide?.getAttribute?.('slide-id') ||
        image?.dataset?.mediaId ||
        '';
      const variantId =
        this.card?.dataset?.inknfiberInitialVariantId ||
        image?.dataset?.variantId ||
        '';

      return imageDescriptor(image, mediaId, variantId);
    }

    findSlide(mediaId) {
      if (!mediaId || !this.mediaContainer?.querySelectorAll) return null;
      return Array.from(this.mediaContainer.querySelectorAll('slideshow-slide[slide-id]')).find(
        (slide) => slide.getAttribute('slide-id') === String(mediaId)
      ) || null;
    }

    applyImageAttributes(image, descriptor) {
      if (!image) return;

      setAttributeOrRemove(image, 'sizes', descriptor.sizes);
      setAttributeOrRemove(image, 'srcset', descriptor.srcset);
      setAttributeOrRemove(image, 'alt', descriptor.alt);
      setAttributeOrRemove(image, 'width', descriptor.width);
      setAttributeOrRemove(image, 'height', descriptor.height);
      setAttributeOrRemove(image, 'data-media-id', descriptor.mediaId);
      setAttributeOrRemove(image, 'data-variant-id', descriptor.variantId);
      if (descriptor.src) image.setAttribute('src', descriptor.src);

      const mediaElement = image.closest?.('.product-media');
      if (mediaElement && descriptor.mediaId) {
        mediaElement.setAttribute('data-media-id', descriptor.mediaId);
      }
    }

    animate(image) {
      if (!image?.classList) return;
      image.classList.remove(SWAP_CLASS);
      void image.offsetWidth;
      image.classList.add(SWAP_CLASS);

      const removeClass = () => image.classList.remove(SWAP_CLASS);
      image.addEventListener?.('animationend', removeClass, { once: true });
      (environment?.setTimeout || setTimeout)(removeClass, 220);
    }

    apply(descriptor) {
      const targetSlide = this.findSlide(descriptor.mediaId);
      const slideshowDisabled = this.slideshow?.getAttribute?.('disabled') === 'true';
      let image = null;

      if (targetSlide && !slideshowDisabled && typeof this.slideshow?.select === 'function') {
        this.slideshow.select({ id: String(descriptor.mediaId) }, undefined, { animate: false });
        image = targetSlide.querySelector?.('img') || null;
      } else {
        image = this.currentImage();
      }

      this.applyImageAttributes(image, descriptor);
      setAttributeOrRemove(this.card, 'data-inknfiber-preview-media-id', descriptor.mediaId);
      setAttributeOrRemove(this.card, 'data-inknfiber-preview-variant-id', descriptor.variantId);
      this.animate(image);
      return image;
    }

    setLoading(isLoading) {
      this.card?.classList?.toggle(LOADING_CLASS, Boolean(isLoading));
    }
  }

  class CardSwatchState {
    constructor(root, options = {}) {
      this.root = root;
      this.card = options.card || root?.closest?.(CARD_SELECTOR) || null;
      this.adapter = options.adapter || new CardMediaAdapter(this.card);
      this.loadImage = options.loadImage || defaultImageLoader;
      this.requestToken = 0;
      this.destroyed = false;
      this.original = this.adapter.originalDescriptor();
      this.committed = this.original;
      this.committedButton = null;
      this.commitFallback = null;
      this.loaded = new Set([descriptorKey(this.original)]);
      this.failed = new Set();
      this.updatePressedState();
    }

    buttons() {
      return Array.from(this.root?.querySelectorAll?.(SWATCH_SELECTOR) || []);
    }

    updatePressedState() {
      this.buttons().forEach((button) => {
        button.setAttribute('aria-pressed', String(button === this.committedButton));
      });
    }

    preview(button) {
      if (this.destroyed || !button) return;
      this.show(descriptorFromButton(button));
    }

    commit(button) {
      if (this.destroyed || !button) return;

      const descriptor = descriptorFromButton(button);
      if (descriptorKey(descriptor) !== descriptorKey(this.committed)) {
        this.commitFallback = {
          descriptor: this.committed,
          button: this.committedButton,
          failedKey: descriptorKey(descriptor),
        };
      }

      this.committed = descriptor;
      this.committedButton = button;
      this.updatePressedState();
      this.show(descriptor);
    }

    restoreCommitted() {
      if (this.destroyed) return;
      this.show(this.committed);
    }

    restoreAfterFailure(descriptor) {
      const failedKey = descriptorKey(descriptor);
      if (this.commitFallback?.failedKey === failedKey && descriptorKey(this.committed) === failedKey) {
        this.committed = this.commitFallback.descriptor;
        this.committedButton = this.commitFallback.button;
        this.commitFallback = null;
        this.updatePressedState();
      }

      this.adapter.apply(this.committed || this.original);
    }

    show(descriptor) {
      const key = descriptorKey(descriptor);
      const token = ++this.requestToken;

      if (this.failed.has(key)) {
        this.restoreAfterFailure(descriptor);
        return;
      }

      if (this.loaded.has(key) || !descriptor.src) {
        this.adapter.setLoading(false);
        this.adapter.apply(descriptor);
        if (this.commitFallback?.failedKey === key) this.commitFallback = null;
        return;
      }

      this.adapter.setLoading(true);
      Promise.resolve(this.loadImage(descriptor))
        .then(() => {
          this.loaded.add(key);
          if (this.destroyed || token !== this.requestToken) return;
          this.adapter.setLoading(false);
          this.adapter.apply(descriptor);
          if (this.commitFallback?.failedKey === key) this.commitFallback = null;
        })
        .catch(() => {
          this.failed.add(key);
          if (this.destroyed || token !== this.requestToken) return;
          this.adapter.setLoading(false);
          this.restoreAfterFailure(descriptor);
        });
    }

    destroy() {
      this.destroyed = true;
      this.requestToken += 1;
      this.adapter.setLoading(false);
    }
  }

  function setExpanded(root, expanded) {
    if (!root) return;
    const limit = Math.max(1, Number(root.dataset?.collapsedLimit || 8));
    const buttons = Array.from(root.querySelectorAll?.(SWATCH_SELECTOR) || []);
    const toggle = root.querySelector?.(TOGGLE_SELECTOR);
    const count = Number(toggle?.dataset?.moreCount || Math.max(0, buttons.length - limit));

    buttons.forEach((button, index) => {
      button.hidden = !expanded && index >= limit;
    });

    if (toggle) {
      const label = expanded ? 'Show fewer colors' : `Show ${count} more colors`;
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.setAttribute('aria-label', label);
      toggle.setAttribute('title', label);
      const text = toggle.querySelector?.('[data-inknfiber-card-swatches-toggle-text]');
      if (text) text.textContent = expanded ? `-${count}` : `+${count}`;
    }

    root.classList?.toggle('is-expanded', Boolean(expanded));
  }

  const stateByRoot = new WeakMap();

  function ensureState(root) {
    if (!root) return null;
    let state = stateByRoot.get(root);
    if (!state) {
      state = new CardSwatchState(root);
      stateByRoot.set(root, state);
    }
    return state;
  }

  function rootsWithin(scope) {
    const roots = [];
    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  }

  function initialize(scope) {
    rootsWithin(scope).forEach((root) => {
      ensureState(root);
      setExpanded(root, root.classList?.contains('is-expanded') || false);
    });
  }

  function disconnect(scope) {
    rootsWithin(scope).forEach((root) => {
      stateByRoot.get(root)?.destroy();
      stateByRoot.delete(root);
    });
  }

  function rootAndState(target) {
    const root = target?.closest?.(ROOT_SELECTOR);
    return { root, state: ensureState(root) };
  }

  function install(documentObject) {
    if (!documentObject || documentObject.inknfiberCardSwatchesInstalled) return;
    documentObject.inknfiberCardSwatchesInstalled = true;

    const onPointerOver = (event) => {
      const button = event.target?.closest?.(SWATCH_SELECTOR);
      if (!button || button.contains?.(event.relatedTarget)) return;
      rootAndState(button).state?.preview(button);
    };

    const onPointerOut = (event) => {
      const button = event.target?.closest?.(SWATCH_SELECTOR);
      if (!button || button.contains?.(event.relatedTarget)) return;
      rootAndState(button).state?.restoreCommitted();
    };

    const onFocusIn = (event) => {
      const button = event.target?.closest?.(SWATCH_SELECTOR);
      if (button) rootAndState(button).state?.preview(button);
    };

    const onFocusOut = (event) => {
      const button = event.target?.closest?.(SWATCH_SELECTOR);
      if (!button || button.contains?.(event.relatedTarget)) return;
      rootAndState(button).state?.restoreCommitted();
    };

    const onClickCapture = (event) => {
      const toggle = event.target?.closest?.(TOGGLE_SELECTOR);
      if (toggle) {
        event.preventDefault();
        event.stopPropagation();
        const root = toggle.closest(ROOT_SELECTOR);
        setExpanded(root, toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      const button = event.target?.closest?.(SWATCH_SELECTOR);
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      rootAndState(button).state?.commit(button);
    };

    const onKeyDownCapture = (event) => {
      const button = event.target?.closest?.(SWATCH_SELECTOR);
      if (!button || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      event.stopPropagation();
      rootAndState(button).state?.commit(button);
    };

    documentObject.addEventListener('pointerover', onPointerOver);
    documentObject.addEventListener('pointerout', onPointerOut);
    documentObject.addEventListener('focusin', onFocusIn);
    documentObject.addEventListener('focusout', onFocusOut);
    documentObject.addEventListener('click', onClickCapture, true);
    documentObject.addEventListener('keydown', onKeyDownCapture, true);
    documentObject.addEventListener('shopify:section:load', (event) => initialize(event.target));
    documentObject.addEventListener('shopify:section:unload', (event) => disconnect(event.target));

    const initializeDocument = () => initialize(documentObject);
    if (documentObject.readyState === 'loading') {
      documentObject.addEventListener('DOMContentLoaded', initializeDocument, { once: true });
    } else {
      initializeDocument();
    }
  }

  return {
    CardMediaAdapter,
    CardSwatchState,
    descriptorFromButton,
    descriptorKey,
    disconnect,
    initialize,
    install,
    setExpanded,
  };
});
